import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MapInfoWindow, MapMarker } from '@angular/google-maps';
import { EmployeeAttendanceDetails, TourLog } from '../../attendance.model';
import moment from 'moment';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

interface AttendanceMapMarker {
  position: google.maps.LatLngLiteral;
  label: string;
  time: string;
  address: string;
}

@Component({
  selector: 'app-attendance-history',
  standalone: false,
  templateUrl: './attendance-history.component.html',
  styleUrl: './attendance-history.component.scss',
})
export class AttendanceHistoryComponent implements OnInit, OnDestroy {
  @Input() employeeId!: string;
  @Input() attendanceList: EmployeeAttendanceDetails[] = [];

  weekStart!: Date;
  weekEnd!: Date;
  weeklyData: any[] = [];
  isDataLoading: boolean = true;
  selectedDay: any = null;
  modalRef!: NgbModalRef;

  // Google Maps — check-in / check-out playback for the selected day
  zoom = 15;
  mapCenter: google.maps.LatLngLiteral = { lat: 20.5937, lng: 78.9629 };
  checkInMarkers: AttendanceMapMarker[] = [];
  checkOutMarkers: AttendanceMapMarker[] = [];
  sessionPaths: google.maps.LatLngLiteral[][] = [];
  travelPaths: google.maps.LatLngLiteral[][] = [];
  hasMapData = false;
  activeMarkerInfo: { label: string; time: string; address: string; type: 'check-in' | 'check-out' } | null = null;
  private _map: google.maps.Map | null = null;

  readonly OFFICE_START_HOUR = 10; // 10 AM
  readonly OFFICE_END_HOUR = 19; // 7 PM
  readonly OFFICE_DURATION_MINUTES = (this.OFFICE_END_HOUR - this.OFFICE_START_HOUR) * 60;

  constructor(private modalService: NgbModal) {}

  ngOnInit(): void {
    const today = new Date();
    this.setWeekRange(today);
  }

  get isPrevBtnDisabled(): boolean {
    if (!this.attendanceList?.length) return true;

    // earliest date (last item if attendanceList is sorted descending)
    const earliestDate = moment(this.attendanceList[this.attendanceList.length - 1].date);

    // disable prev if weekStart is before or same as earliest
    return moment(this.weekStart).isSameOrBefore(earliestDate, 'day');
  }

  get isNextBtnDisabled(): boolean {
    if (!this.attendanceList?.length) return true;

    // latest date (first item if sorted descending)
    const latestDate = moment(this.attendanceList[0].date);

    // disable next if weekEnd is after or same as latest
    return moment(this.weekEnd).isSameOrAfter(latestDate, 'day');
  }

  protected prevWeek() {
    const prev = new Date(this.weekStart);
    prev.setDate(prev.getDate() - 7);

    this.setWeekRange(prev);
    this.filterWeekData();
  }

  protected nextWeek() {
    const next = new Date(this.weekStart);
    next.setDate(next.getDate() + 7);
    this.setWeekRange(next);
    this.filterWeekData();
  }

  setWeekRange(referenceDate: Date): void {
    const reference = new Date(referenceDate);
    const dayIndex = (reference.getDay() + 6) % 7; // Convert to Monday start
    this.weekStart = new Date(reference.setDate(reference.getDate() - dayIndex));
    this.weekEnd = new Date(this.weekStart);
    this.weekEnd.setDate(this.weekStart.getDate() + 6);

    this.filterWeekData();
    this.isDataLoading = false;
  }

  filterWeekData(): void {
    const startDate = moment(this.weekStart).startOf('day');
    const endDate = moment(this.weekEnd).startOf('day');

    const groupedByDate = new Map<string, EmployeeAttendanceDetails[]>();

    this.attendanceList.forEach(entry => {
      const entryDate = moment(entry.date, 'YYYY-MM-DD').format('YYYY-MM-DD');
      const startStr = startDate.format('YYYY-MM-DD');
      const endStr = endDate.format('YYYY-MM-DD');

      if (entryDate >= startStr && entryDate <= endStr) {
        if (!groupedByDate.has(entryDate)) {
          groupedByDate.set(entryDate, []);
        }
        groupedByDate.get(entryDate)?.push(entry);
      }
    });

    this.weeklyData = [];

    for (let i = 0; i < 7; i++) {
      const current = startDate.clone().add(i, 'days');
      const dateStr = current.format('YYYY-MM-DD');
      const logs = groupedByDate.get(dateStr) ?? [];
      const flatLogs = logs.flatMap(e => e.logs);

      // --- Compute total worked hours ---
      let totalMinutes = 0;
      flatLogs.forEach(log => {
        if (log.check_in && log.check_out) {
          const checkIn = moment(log.check_in);
          const checkOut = moment(log.check_out);

          if (checkIn.isValid() && checkOut.isValid()) {
            const duration = checkOut.diff(checkIn, 'minutes');
            if (duration > 0) {
              totalMinutes += duration;
            }
          }
        }
      });

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const totalTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

      // --- Compute first check-in ---
      const firstCheckIn =
        flatLogs
          .map(l => l.check_in)
          .filter(ci => ci !== null)
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] || null;

      // --- Compute last check-out ---
      const lastCheckOut =
        flatLogs
          .map(l => l.check_out)
          .filter(co => co !== null)
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
          .pop() || null;

      this.weeklyData.push({
        date: current.toDate(),
        logs: flatLogs,
        total_worked_hours: totalMinutes > 0 ? totalTime : '00:00',
        firstCheckIn,
        lastCheckOut,
      });
    }
  }

  openLogsModal(day: any, modalTemplate: any) {
    this.selectedDay = day;
    this._buildMapData(day?.logs ?? []);
    this.modalRef = this.modalService.open(modalTemplate, { size: 'lg', backdrop: 'static' });
  }

  private _buildMapData(logs: TourLog[]): void {
    this.checkInMarkers = [];
    this.checkOutMarkers = [];
    this.sessionPaths = [];
    this.travelPaths = [];
    this.activeMarkerInfo = null;
    this._map = null;

    logs.forEach((log, i) => {
      if (!log.check_in_location?.latitude) return;

      const ciPos: google.maps.LatLngLiteral = {
        lat: log.check_in_location.latitude,
        lng: log.check_in_location.longitude,
      };
      this.checkInMarkers.push({
        position: ciPos,
        label: `S${i + 1}`,
        time: log.check_in,
        address: log.check_in_location.address ?? '',
      });

      if (i === 0) {
        this.mapCenter = ciPos;
      }

      if (!log.check_out_location?.latitude) return;

      const coPos: google.maps.LatLngLiteral = {
        lat: log.check_out_location.latitude,
        lng: log.check_out_location.longitude,
      };
      this.checkOutMarkers.push({
        position: coPos,
        label: `E${i + 1}`,
        time: log.check_out ?? '',
        address: log.check_out_location.address ?? '',
      });
      this.sessionPaths.push([ciPos, coPos]);

      // Connect E(i) → S(i+1) if the next session has a check-in location
      const nextLog = logs[i + 1];
      if (nextLog?.check_in_location?.latitude) {
        this.travelPaths.push([
          coPos,
          { lat: nextLog.check_in_location.latitude, lng: nextLog.check_in_location.longitude },
        ]);
      }
    });

    this.hasMapData = this.checkInMarkers.length > 0;
  }

  protected onMapInitialized(map: google.maps.Map): void {
    this._map = map;
    this._fitMapToMarkers();
  }

  private _fitMapToMarkers(): void {
    const positions = [...this.checkInMarkers, ...this.checkOutMarkers].map(m => m.position);
    if (!this._map || positions.length < 2) return;

    const bounds = new google.maps.LatLngBounds();
    positions.forEach(p => bounds.extend(p));
    this._map.fitBounds(bounds, 48);
  }

  protected onMarkerHover(
    infoWindow: MapInfoWindow,
    marker: MapMarker,
    info: AttendanceMapMarker,
    type: 'check-in' | 'check-out'
  ): void {
    this.activeMarkerInfo = { label: info.label, time: info.time, address: info.address, type };
    infoWindow.open(marker);
  }

  protected onMarkerOut(infoWindow: MapInfoWindow): void {
    infoWindow.close();
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
  }
}
